import { Graphics } from 'pixi.js';
import { gsap } from 'gsap';
import { moveAndCollide, type AABB } from '../engine/Physics';
import type { BossAttack, BossDef, BossPhase, GameConfig } from '../types';
import { Bullet } from './Bullet';
import type { Player } from './Player';

const TERMINAL_VY = 18;
const CHARGE_DURATION_MS = 800;

export interface BossContext {
  cfg: GameConfig;
  player: Player;
  solids: readonly AABB[];
  spawnBullet(b: Bullet): void;
  stepDt: number;
  arenaWidth: number;
}

function hex(c: string): number {
  return parseInt(c.replace('#', ''), 16);
}

export class Boss {
  alive = true;
  hp: number;
  x: number;
  y: number;
  vx = 0;
  vy = 0;
  width: number;
  height: number;
  gfx = new Graphics();

  private phases: BossPhase[];
  private currentPhase: BossPhase;
  private cooldowns = new Map<string, number>();
  private rageActive = false;
  private rageMs = 0;
  private chargeActive = false;
  private chargeMs = 0;
  private direction: 1 | -1 = 1;
  private erraticTimer = 0;
  private onGround = false;

  constructor(public def: BossDef) {
    this.hp = def.hp;
    this.x = def.x;
    this.y = def.y;
    this.width = def.width;
    this.height = def.height;
    this.phases = def.phases;
    this.currentPhase = this.selectPhase();
    this.draw(def.color);
    this.initCooldowns();
  }

  bounds(): AABB {
    return { x: this.x, y: this.y, w: this.width, h: this.height };
  }

  syncGfx(): void {
    this.gfx.x = this.x + this.width / 2;
    this.gfx.y = this.y + this.height / 2;
  }

  destroy(): void {
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }

  /** Returns true if killed by this hit. */
  hit(): boolean {
    if (!this.alive) return false;
    this.hp -= 1;
    gsap.killTweensOf(this.gfx);
    gsap.to(this.gfx, {
      tint: 0xffaaaa,
      duration: 0.06,
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        this.gfx.tint = 0xffffff;
      },
    });
    if (this.hp <= 0) {
      this.alive = false;
      gsap.to(this.gfx.scale, { x: 0, y: 0, duration: 0.6, ease: 'back.in(2)' });
      gsap.to(this.gfx, { rotation: Math.PI, alpha: 0, duration: 0.6 });
      return true;
    }
    return false;
  }

  step(ctx: BossContext): void {
    if (!this.alive) return;
    const stepMs = ctx.stepDt * 1000;

    const newPhase = this.selectPhase();
    if (newPhase.phase !== this.currentPhase.phase) {
      this.currentPhase = newPhase;
      gsap.fromTo(
        this.gfx.scale,
        { x: 1.3, y: 1.3 },
        { x: 1, y: 1, duration: 0.4, ease: 'back.out(2)' },
      );
    }

    if (this.rageActive) {
      this.rageMs -= stepMs;
      if (this.rageMs <= 0) this.rageActive = false;
    }

    if (this.chargeActive) {
      this.chargeMs -= stepMs;
      if (this.chargeMs <= 0) {
        this.chargeActive = false;
        this.vx = 0;
      }
    }

    if (!this.chargeActive) {
      const speedMul = this.rageActive ? 1.5 : 1;
      const speed = this.currentPhase.moveSpeed * speedMul;
      if (this.currentPhase.movePattern === 'erratic') {
        this.erraticTimer -= stepMs;
        if (this.erraticTimer <= 0) {
          this.direction = Math.random() < 0.5 ? -1 : 1;
          this.erraticTimer = 400 + Math.random() * 600;
        }
      }
      this.vx = this.direction * speed;
    }

    this.vy += ctx.cfg.gravity;
    if (this.vy > TERMINAL_VY) this.vy = TERMINAL_VY;

    const box = this.bounds();
    const res = moveAndCollide(box, this.vx, this.vy, ctx.solids);
    this.x = box.x;
    this.y = box.y;

    if (res.hitY) {
      const wasFalling = this.vy > 0;
      this.vy = 0;
      if (wasFalling) this.onGround = true;
    } else {
      this.onGround = false;
    }
    if (res.hitX) {
      this.direction = -this.direction as 1 | -1;
      if (this.chargeActive) {
        this.chargeActive = false;
        this.vx = 0;
      }
    }

    if (this.x <= 0) {
      this.x = 0;
      this.direction = 1;
      if (this.chargeActive) {
        this.chargeActive = false;
        this.vx = 0;
      }
    } else if (this.x + this.width >= ctx.arenaWidth) {
      this.x = ctx.arenaWidth - this.width;
      this.direction = -1;
      if (this.chargeActive) {
        this.chargeActive = false;
        this.vx = 0;
      }
    }

    const cdMul = this.rageActive ? 0.5 : 1;
    for (const attack of this.currentPhase.attacks) {
      const cd = (this.cooldowns.get(attack.type) ?? attack.interval) - stepMs;
      this.cooldowns.set(attack.type, cd);
      if (cd <= 0) {
        this.fireAttack(attack, ctx);
        this.cooldowns.set(attack.type, attack.interval * cdMul);
      }
    }
  }

  private fireAttack(a: BossAttack, ctx: BossContext): void {
    switch (a.type) {
      case 'bulletSpray':
        this.attackSpray(a, ctx);
        break;
      case 'bulletSingle':
        this.attackSpray({ ...a, count: 1, spread: 0 }, ctx);
        break;
      case 'jump':
        this.attackJump(a);
        break;
      case 'charge':
        this.attackCharge(a, ctx);
        break;
      case 'rageMode':
        this.attackRage(a);
        break;
    }
  }

  private attackSpray(a: BossAttack, ctx: BossContext): void {
    const cx = this.x + this.width / 2;
    const cy = this.y + this.height / 2;
    const px = ctx.player.pos.x + ctx.player.width / 2;
    const py = ctx.player.pos.y + ctx.player.height / 2;
    const aim = Math.atan2(py - cy, px - cx);
    const count = a.count ?? 1;
    const spreadRad = ((a.spread ?? 0) * Math.PI) / 180;
    const speed = a.speed ?? 4;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const angle = aim + (t - 0.5) * spreadRad;
      const dx = Math.cos(angle);
      const dy = Math.sin(angle);
      ctx.spawnBullet(new Bullet(cx + dx * 16, cy + dy * 16, dx * speed, dy * speed, 'enemy'));
    }
  }

  private attackJump(a: BossAttack): void {
    if (!this.onGround) return;
    this.vy = a.jumpForce ?? -12;
    this.onGround = false;
  }

  private attackCharge(a: BossAttack, ctx: BossContext): void {
    if (!this.onGround) return;
    const playerCx = ctx.player.pos.x + ctx.player.width / 2;
    const bossCx = this.x + this.width / 2;
    this.direction = playerCx > bossCx ? 1 : -1;
    this.chargeActive = true;
    this.chargeMs = CHARGE_DURATION_MS;
    this.vx = this.direction * (a.speed ?? 6);
  }

  private attackRage(a: BossAttack): void {
    this.rageActive = true;
    this.rageMs = a.duration ?? 3000;
    gsap.killTweensOf(this.gfx);
    gsap.to(this.gfx, {
      tint: 0xff5555,
      duration: 0.15,
      yoyo: true,
      repeat: 7,
      onComplete: () => {
        this.gfx.tint = 0xffffff;
      },
    });
  }

  private initCooldowns(): void {
    for (const ph of this.phases) {
      for (const a of ph.attacks) {
        if (!this.cooldowns.has(a.type)) this.cooldowns.set(a.type, a.interval);
      }
    }
  }

  private selectPhase(): BossPhase {
    let chosen = this.phases[0]!;
    for (const p of this.phases) {
      if (this.hp <= p.hpThreshold) chosen = p;
    }
    return chosen;
  }

  private draw(color: string): void {
    const c = hex(color);
    const r = this.width / 2;
    const g = this.gfx;
    g.clear();
    g.circle(0, 0, r).fill({ color: c }).stroke({ color: 0x000000, width: 3 });
    g.poly([-r * 0.4, -r, 0, -r * 1.2, r * 0.4, -r])
      .fill({ color: 0x6abf6a })
      .stroke({ color: 0x000000, width: 2 });
    g.circle(-r * 0.3, -r * 0.1, r * 0.14).fill({ color: 0xffffff });
    g.circle(r * 0.3, -r * 0.1, r * 0.14).fill({ color: 0xffffff });
    g.circle(-r * 0.3, -r * 0.05, r * 0.08).fill({ color: 0x000000 });
    g.circle(r * 0.3, -r * 0.05, r * 0.08).fill({ color: 0x000000 });
    g.poly([-r * 0.55, -r * 0.4, -r * 0.05, -r * 0.2, -r * 0.55, -r * 0.15])
      .fill({ color: 0x000000 });
    g.poly([r * 0.55, -r * 0.4, r * 0.05, -r * 0.2, r * 0.55, -r * 0.15])
      .fill({ color: 0x000000 });
    g.moveTo(-r * 0.45, r * 0.35)
      .quadraticCurveTo(0, r * 0.05, r * 0.45, r * 0.35)
      .stroke({ color: 0x000000, width: 5 });
    g.poly([-r * 0.3, r * 0.4, -r * 0.2, r * 0.35, -r * 0.1, r * 0.4])
      .fill({ color: 0xffffff });
  }
}
